// The engine draws on demand, so a second with nothing moving reports 0 fps in its `stats`. The
// HUD shows the frame rate of the last motion instead. While the camera moves the engine sends one
// `camera` event per frame it draws (and only then), so the gaps between them time its frames;
// while the clock plays, every frame is drawn and `stats` counts them.

/** A pause this long between camera events ends a motion. Long, so that a slow engine (a few fps
 *  on a loaded machine) still reads as one motion rather than as many single frames. */
export const MOTION_GAP_MS = 1000;
/** Frames a motion needs before its rate is read. */
export const MOTION_MIN_FRAMES = 8;
/** The rate is read from the motion's latest frame gaps, this many at most. */
const WINDOW = 64;

export class MotionMeter {
  private last = Number.NEGATIVE_INFINITY;
  private gaps: number[] = [];
  /** The last motion's frame rate, rounded; null until something has moved. */
  fps: number | null = null;

  /** A camera event at `t` ms. Returns whether the reading changed. */
  frame(t: number): boolean {
    const gap = t - this.last;
    this.last = t;
    if (gap > MOTION_GAP_MS) {
      this.gaps = [];
      return false;
    }
    if (gap <= 0) return false;
    this.gaps.push(gap);
    if (this.gaps.length > WINDOW) this.gaps.shift();
    if (this.gaps.length < MOTION_MIN_FRAMES - 1) return false;
    // The median gap: a hold mid-drag or one late frame doesn't drag the rate down.
    const sorted = [...this.gaps].sort((a, b) => a - b);
    return this.set(1000 / sorted[sorted.length >> 1]!);
  }

  /** A `stats` event while the clock plays. An idle second (0 fps) never replaces the reading. */
  playing(fps: number): boolean {
    return fps >= 1 ? this.set(fps) : false;
  }

  private set(fps: number): boolean {
    const r = Math.round(fps);
    if (r === this.fps) return false;
    this.fps = r;
    return true;
  }
}
